import { useGuestResource } from '@hooks/useGuestResource';
import { addOrModifyGuest } from '@model/event-guest';
import DashboardGuestDetail from '@quickService/DashboardGuestDetail';
import DashboardGuestInviteCode from '@quickService/DashboardGuestInviteCode';
import {
  Button,
  Drawer,
  Form,
  Input,
  InputAdornment,
  message,
  Radio,
  Select,
} from '@tencent/tea-component';
import { AppContext } from '@utils/browser/app-context';
import copyToClipboard from '@utils/copy-to-clipboard';
import dataReport from '@utils/data-report';
import { Validator } from '@utils/validator';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FieldRenderProps, useField, useForm } from 'react-final-form-hooks';
import confirm from 'src/components/saleModal/method';
import useProgressChange from 'src/hooks/useProccessChange';
import { GuestItem } from '../index.page';
import GuestAvatarUpload, { CosInfo } from 'src/components/HeadUpload/upload';
import thumpImg from 'src/assets/imgs/upload-thumb.png';
import styles from './index.module.scss';
import { IS_I18N } from 'src/voov';
import VOOV_COUNTRY_DATA from '@utils/data.voov';
import COUNTRY_DATA from '@utils/data';

const { TextArea } = Input;

export interface EditGuestProps {
  onClose: Function;
  eventId: string;
  visible: boolean;
  showMask?: boolean;
  guestInfo?: GuestItem | null;
  hasGoback?: boolean;
}

const { isPhone, isUserName, isPhoneLikeCode } = Validator;
const defaultGuest: GuestItem = {
  name: '',
  email: '',
  phone: '',
  company: '',
  position: '',
  area_code: '+86',
  describe: '',
  // invite_type: 0 手机号，1 链接
  invite_type: 1,
  invite_code: '',
} as unknown as GuestItem;
let isPending = false;

export function isInviteByLink(inviteType: number) {
  return inviteType === 1;
}

export function getInviteLink(eventId: string, inviteCode: string) {
  return `${location.origin}/events/host/invite?invite_code=${inviteCode}&event_id=${eventId}`;
}

const countryData = IS_I18N ? VOOV_COUNTRY_DATA : COUNTRY_DATA;

interface SelectAreaCodeProps {
  value: string;
  onChange: (value: string) => void;
}

export function getMaxLength(area = '86') {
  if (area === '86' || area === '+86') {
    return 11;
  }
  return 20;
}

// 选择手机区号
export const SelectAreaCode = ({ value, onChange }: SelectAreaCodeProps) => {
  const countryOptions = countryData.map((item) => {
    const { areaCode, country, name } = item;
    const key = `${country}_${name}_${areaCode}`;
    return {
      text: (
        <div className={styles.selectCountryItem} key={key}>
          <span title={item.name}>{name}</span>
          <span>+{areaCode}</span>
        </div>
      ),
      value: areaCode,
    };
  });

  const valueAdapter = useMemo(() => {
    if (value.startsWith('+')) {
      return value.slice(1);
    }
    return value;
  }, [value]);

  const displayValue = useMemo(() => {
    if (value.startsWith('+')) {
      return value;
    }
    return `+${value}`;
  }, [value]);

  return (
    <Select
      placeholder={valueAdapter}
      listHeight={300}
      type={'simulate'}
      appearance={'button'}
      searchable={true}
      className={styles.selectCountry}
      boxClassName={styles.selectCountryBox}
      value={valueAdapter}
      options={countryOptions}
      onChange={(s) => onChange(s)}
      filter={(inputValue, option) => option.value.includes(inputValue)}
      button={displayValue}
    />
  );
};

/**
 * 编辑嘉宾 + 新增嘉宾
 */
const EditGuest: React.FC<EditGuestProps> = (props) => {
  const { guestInfo, visible, onClose, eventId, showMask, hasGoback = false } = props;
  const isEditGuest = useMemo(() => !!guestInfo?.guest_id, [guestInfo?.guest_id]);
  const { hostDetail } = useContext(AppContext);
  const { form, handleSubmit, submitting } = useForm({
    initialValuesEqual: () => true,
    initialValues: guestInfo || defaultGuest,
    validate: ({ name, company, phone, invite_type, area_code }) => {
      const errors: Partial<Record<keyof GuestItem, string>> = {};
      if (invite_type === 0) {
        if (area_code === '+86' && !isPhone(phone)) {
          errors.phone = '请检查填写正确的手机号';
        }
        if (area_code !== '+86' && !isPhoneLikeCode(phone)) {
          errors.phone = '请检查填写正确的手机号';
        }
      }
      if (!isUserName(name)) {
        errors.name = '请输入嘉宾名称';
      }
      if (!company.length) {
        errors.company = '请输入头衔1';
      }
      return errors;
    },
    onSubmit: () => {
      handleSubmitForm();
    },
  });

  const fields = ['name', 'company', 'position', 'email', 'phone', 'describe', 'area_code'];
  const name = useField('name', form);
  const company = useField('company', form);
  const position = useField('position', form);
  const email = useField('email', form);
  const phone = useField('phone', form);
  const describe = useField('describe', form);
  const countryCode = useField('area_code', form);
  const inviteType = useField('invite_type', form);
  const inviteCode = useField('invite_code', form);
  const [avatarUrl, setAvatar] = useState(guestInfo?.avatar || '');
  const [avatarCosKey, setCosKey] = useState('');
  const { validateStatus } = useProgressChange();

  const { mutate } = useGuestResource(eventId);

  const isInviteByPhone = useMemo(
    () => Number(inviteType.input.value) === 0,
    [inviteType.input.value],
  );

  useEffect(() => {
    if (visible && form) {
      const initValues = guestInfo || defaultGuest;
      form.initialize(initValues);
      Object.keys(initValues).forEach((key: any) => {
        if (fields.includes(key)) {
          form.resetFieldState(key);
        }
      });
      setAvatar(guestInfo?.avatar || '');
      setCosKey('');
    }
  }, [form, guestInfo, visible]);

  const handleSubmitForm = useCallback(() => {
    const formValue = form.getState().values;
    const guestData: any = {
      ...formValue,
      event_id: eventId || '',
      guest_id: guestInfo?.guest_id,
      area_code: countryCode.input.value,
      avatar: avatarUrl,
      avatar_cos_key: avatarCosKey,
    };
    if (!guestData?.guest_id) {
      Object.assign(guestData, { host_id: hostDetail?.host_id });
    }
    // 嘉宾保存埋点
    dataReport('e#event_panalist_create#save#click', {
      activity_id: eventId,
      name_guest: formValue.name,
      position1: formValue.company,
      position2: formValue.position,
      introduce: formValue.describe,
    });

    isPending = true;
    addOrModifyGuest(guestData)
      .then((res) => {
        message.success({ content: '保存成功' });
        onClose(res, guestInfo?.guest_id);
        isPending = false;
        validateStatus();
        mutate();
      })
      .catch((e) => {
        message.error({ content: e.msg || '网络出错, 保存失败！' });
        isPending = false;
      });
  }, [eventId, guestInfo, form, avatarCosKey, avatarUrl, countryCode, onClose]);

  const handleCloseModal = useCallback(() => {
    const hasChanged = Object.keys(form.getState().dirtyFields).length > 0;
    if (hasChanged) {
      confirm({
        title: '信息没保存',
        content: '信息还没保存，确定离开吗？',
      })
        .then(() => {
          onClose(true);
        })
        .catch(() => {
          console.log('用户点取消');
        });
    } else {
      onClose(true);
    }
  }, [form, onClose]);

  const [formError, setFormError] = useState(false);

  useEffect(() => {
    if (name.meta.invalid || email.meta.invalid || company.meta.invalid) {
      setFormError(true);
      return;
    }
    if (isInviteByPhone) {
      if (phone.meta.invalid) {
        setFormError(true);
      } else {
        setFormError(false);
      }
      return;
    }
    setFormError(false);
  }, [
    phone.meta,
    company.meta,
    name.meta,
    email.meta,
    phone.input.value,
    company.input.value,
    name.input.value,
    email.input.value,
    inviteType.input.value,
    setFormError,
  ]);

  const onUploadComplete = (res: CosInfo) => {
    setAvatar(res.url);
    setCosKey(res.cosId);
  };

  const onInputChange = (call: (value: string) => void) => (v: string) => {
    call(v.trim());
  };

  const onAreaCodeChange = (v: string) => {
    countryCode.input.onChange(`+${v.trim()}`);
  };

  const getStatus = (item: FieldRenderProps) => {
    if (item.meta.invalid) {
      if (item.meta.touched) {
        return 'error';
      }
      if (item.meta.validating) {
        return 'validating';
      }
      return undefined;
    }
    return undefined;
  };

  const getMessage = (item: FieldRenderProps) => {
    if (item.meta.touched && item.meta.invalid) {
      return item.meta.error;
    }
    return '';
  };

  const renderFooter = () => (
    <form
      onSubmit={(event) => {
        event.stopPropagation();
        event.preventDefault();
        if (isPending) {
          return;
        }
        handleSubmit();
      }}
    >
      <Form.Action>
        <Button
          type="primary"
          htmlType="submit"
          loading={submitting}
          disabled={formError}
          className="size-s"
        >
          保存
        </Button>
      </Form.Action>
    </form>
  );

  const [title, subTitle] = useMemo(() => {
    const title = guestInfo?.guest_id ? '编辑嘉宾' : '添加嘉宾';
    const subTitle = guestInfo?.guest_id
      ? '在此管理规定嘉宾详细信息'
      : '在此添加和管理活动嘉宾信息详情';
    if (hasGoback) {
      // 如果有返回按钮
      return [
        <div className={styles.goBack} onClick={handleCloseModal}>
          <i className="iconfont icon-arrow-left"></i>
          <span>返回</span>
        </div>,
        <div className={styles.innerHeader}>
          <div className={styles.mainTitle}>{title}</div>
          <div className="tea-card__subtitle">{subTitle}</div>
        </div>,
      ];
    }
    return [<div className={styles.mainTitle}>{title}</div>, subTitle];
  }, [hasGoback, guestInfo]);

  useEffect(() => {
    // 如果是编辑的就出来吧
    if (visible && guestInfo?.guest_id) {
      DashboardGuestDetail({
        event_id: eventId,
        guest_id: guestInfo?.guest_id,
      }).then((res) => {
        if (res.code === 0) {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          const { phone, email, invite_code, invite_type, area_code } = res.data;
          form.change('phone', phone);
          form.change('email', email);
          form.change('invite_code', invite_code);
          form.change('invite_type', invite_type);
          form.change('area_code', area_code);
        }
      });
    } else {
      fetchInviteCode();
    }
  }, [visible, guestInfo?.guest_id]);

  const onGroupChange = (value: string) => {
    inviteType.input.onChange(Number(value));
  };

  const inviteTypeMessage = useMemo(() => {
    if (Number(inviteType.input.value) === 0) {
      if (phone.meta.invalid && phone.meta.touched && phone.meta.error) {
        return phone.meta.error;
      }
      return '此号码用于给嘉宾发送专属门票';
    }
    return '点击链接的用户确认后成为嘉宾，需复制以上链接发送给嘉宾';
  }, [inviteType.input.value, phone.meta]);

  const inviteTypeStatus = useMemo(() => {
    if (isInviteByPhone && phone.meta.invalid) {
      if (phone.meta.touched) {
        return 'error';
      }
      if (phone.meta.validating) {
        return 'validating';
      }
    }
    return undefined;
  }, [inviteType.input.value, phone.meta]);

  const inviteLink = useMemo(() => {
    if (inviteCode.input.value) {
      return getInviteLink(eventId, inviteCode.input.value);
    }
    return '';
  }, [inviteCode.input.value]);

  const onSpanClick = useCallback(() => {
    copyToClipboard(inviteLink);
    message.success({ content: '复制成功' });
  }, [inviteLink]);

  const fetchInviteCode = () => {
    DashboardGuestInviteCode({
      event_id: eventId,
    }).then((resp) => {
      if (resp?.code === 0 && resp?.data.invite_code) {
        form.change('invite_code', resp?.data.invite_code);
      } else {
        message.error({ content: resp?.msg || '获取邀请码失败' });
      }
    });
  };

  return (
    <div>
      <Drawer
        placement="right"
        visible={visible}
        className={styles.addGuest}
        showMask={showMask}
        onClose={handleCloseModal}
        subtitle={subTitle}
        title={title}
        footer={renderFooter()}
      >
        <GuestAvatarUpload
          defaultHeadUrl={avatarUrl || thumpImg}
          onUploadComplete={onUploadComplete}
          isInForm
          uploadMsg="推荐使用240px*240px尺寸，支持png/jpg/jpeg格式，大小不超过5M"
          minWidth={240}
          minHeight={240}
          className={styles.guestUpload}
        />
        <Form style={{ marginTop: '30px' }}>
          <Form.Item label="嘉宾姓名" required status={getStatus(name)} message={getMessage(name)}>
            <Input
              {...name.input}
              autoComplete="off"
              size="full"
              maxLength={20}
              placeholder=""
              onChange={onInputChange(name.input.onChange)}
            />
          </Form.Item>
          <Form.Item
            required
            label="头衔1"
            status={getStatus(company)}
            message={getMessage(company)}
          >
            <Input
              {...company.input}
              size="full"
              autoComplete="off"
              maxLength={20}
              onChange={onInputChange(company.input.onChange)}
            />
          </Form.Item>
          <Form.Item label="头衔2" status={getStatus(position)}>
            <Input
              {...position.input}
              size="full"
              autoComplete="off"
              maxLength={20}
              onChange={onInputChange(position.input.onChange)}
            />
          </Form.Item>
          <Form.Item
            label="邀请方式"
            required
            status={inviteTypeStatus}
            message={inviteTypeMessage}
            className={styles.inviteItem}
          >
            <div>
              <div>
                <Radio.Group
                  disabled={isEditGuest}
                  value={String(inviteType.input.value)}
                  onChange={onGroupChange}
                >
                  <Radio name="1">链接邀请</Radio>
                  <Radio name="0">手机号邀请</Radio>
                </Radio.Group>
              </div>
              {isInviteByPhone ? (
                <div className={styles.inviteContent}>
                  <InputAdornment
                    before={
                      <SelectAreaCode value={countryCode.input.value} onChange={onAreaCodeChange} />
                    }
                    style={{ width: '100%' }}
                  >
                    <Input
                      {...phone.input}
                      autoComplete="off"
                      size="full"
                      type="tel"
                      maxLength={getMaxLength(countryCode.input.value)}
                      onChange={onInputChange(phone.input.onChange)}
                    />
                  </InputAdornment>
                </div>
              ) : (
                <div className={styles.inviteContent}>
                  <span>{inviteLink}</span>
                  <span className={styles.copy} onClick={onSpanClick}>
                    复制
                  </span>
                </div>
              )}
            </div>
          </Form.Item>
          <Form.Item
            label="邮箱"
            status={getStatus(email)}
            message={getMessage(email) || '此邮箱用于给嘉宾发送专属门票'}
          >
            <Input
              {...email.input}
              size="full"
              autoComplete="off"
              onChange={onInputChange(email.input.onChange)}
            />
          </Form.Item>
          <Form.Item label="基本介绍" status={getStatus(describe)}>
            <TextArea
              {...describe.input}
              size="full"
              autoComplete="off"
              showCount
              placeholder="输入文本内容"
              maxLength={150}
            />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
};

export default EditGuest;
